import { GeoJsonLayer } from '@deck.gl/layers';
import proj4 from 'proj4';
import bbox from '@turf/bbox';
import { showMapConfirmToast } from "../utils/Toast/mapToast";

const extractEPSGCode = (srsName) => {
  return srsName && srsName.includes('#') ? 'EPSG:' + srsName.split('#')[1] : 'EPSG:4326';
};

const parsePosList = (posList) => {
  if (!posList) return [];

  const coords = posList.textContent.trim().split(/\s+/);
  const parsedCoords = [];

  for (let i = 0; i < coords.length; i += 2) {
    const x = parseFloat(coords[i]);
    const y = parseFloat(coords[i + 1]);

    if (!isNaN(x) && !isNaN(y)) {
      parsedCoords.push([x, y]);
    }
  }

  return parsedCoords;
};

const transformCoordinates = (coords, sourceCRS, targetCRS) => {
  return coords.map(coord => {
    try {
      const [x, y] = coord;
      const [lon, lat] = proj4(sourceCRS, targetCRS, [x, y]);
      return [lon, lat];
    } catch (error) {
      console.error('Error transforming coordinates:', error);
      return coord;
    }
  });
};

const parsePolygon = (polygonNode) => {
  const posList = polygonNode.getElementsByTagNameNS('*', 'posList')[0];
  return posList ? parsePosList(posList) : [];
};

const parseMultiSurface = (multiSurfaceNode) => {
  const polygons = multiSurfaceNode.getElementsByTagNameNS('*', 'Polygon');
  return Array.from(polygons).map(polygon => parsePolygon(polygon));
};

const parseMultiCurve = (multiCurveNode) => {
  const lineStrings = multiCurveNode.getElementsByTagNameNS('*', 'LineString');

  return Array.from(lineStrings).map(lineString =>
    parsePosList(lineString.getElementsByTagNameNS('*', 'posList')[0])
  );
};

const extractAttributes = (feature) => {
  const attributes = {};
  const children = Array.from(feature.children);

  children.forEach(child => {
    const name = child.localName;

    if (name !== 'geometry' && name !== 'the_geom') {
      attributes[name] = child.textContent || null;
    }
  });

  return attributes;
};

const parseGMLToGeoJSON = (xmlDoc) => {
  const geojson = {
    type: 'FeatureCollection',
    features: [],
  };

  const featureMembers = [
    ...Array.from(xmlDoc.getElementsByTagNameNS('*', 'featureMember')),
    ...Array.from(xmlDoc.getElementsByTagNameNS('*', 'member')),
  ];

  Array.from(featureMembers).forEach(member => {
    const feature = member.firstElementChild;
    if (!feature) return;

    const geometryTag = feature.getElementsByTagName('geometry')[0];

    if (geometryTag) {
      try {
        const text = geometryTag.textContent?.trim();

        if (text.startsWith('{') && text.endsWith('}')) {
          const geometry = JSON.parse(text);

          geojson.features.push({
            type: 'Feature',
            geometry,
            properties: extractAttributes(feature),
          });

          return;
        }
      } catch (e) {
        console.warn('Neuspješno parsiranje GeoJSON geometrije, pokušavam GML...', e);
      }
    }

    const geometryNode =
      feature.getElementsByTagNameNS('*', 'MultiSurface')[0] ||
      feature.getElementsByTagNameNS('*', 'Polygon')[0] ||
      feature.getElementsByTagNameNS('*', 'MultiPolygon')[0] ||
      feature.getElementsByTagNameNS('*', 'MultiCurve')[0] ||
      feature.getElementsByTagNameNS('*', 'LineString')[0] ||
      feature.getElementsByTagNameNS('*', 'Point')[0];

    if (geometryNode) {
      let geometryType;
      let coordinates = [];

      const srsName = geometryNode.getAttribute('srsName');
      const sourceCRS = extractEPSGCode(srsName);

      if (
        geometryNode.localName === 'MultiSurface' ||
        geometryNode.localName === 'Polygon' ||
        geometryNode.localName === 'MultiPolygon'
      ) {
        geometryType = 'Polygon';
        coordinates = parseMultiSurface(geometryNode);
      } else if (geometryNode.localName === 'MultiCurve') {
        geometryType = 'MultiLineString';
        coordinates = parseMultiCurve(geometryNode);
      } else if (geometryNode.localName === 'LineString') {
        geometryType = 'LineString';
        coordinates = parsePosList(
          geometryNode.getElementsByTagNameNS('*', 'posList')[0]
        );
      } else if (geometryNode.localName === 'Point') {
        geometryType = 'Point';
        coordinates = parsePosList(
          geometryNode.getElementsByTagNameNS('*', 'pos')[0]
        );
      }

      if (coordinates.length > 0) {
        const transformedCoords = transformCoordinates(
          coordinates.flat(),
          sourceCRS,
          'EPSG:4326'
        );

        geojson.features.push({
          type: 'Feature',
          geometry: {
            type: geometryType,
            coordinates:
              geometryType === 'Polygon' || geometryType === 'MultiLineString'
                ? [transformedCoords]
                : transformedCoords,
          },
          properties: extractAttributes(feature),
        });
      }
    }
  });

  return geojson;
};

const handleFileOpen = async (file) => {
  try {
    if (!file) throw new Error('No file provided');

    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onload = () => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(reader.result, 'application/xml');

        try {
          const geojson = parseGMLToGeoJSON(xmlDoc);
          resolve(geojson);
        } catch (error) {
          console.error('Failed to convert GML to GeoJSON:', error);
          reject(new Error('Failed to convert GML to GeoJSON'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  } catch (error) {
    console.error('Error processing file:', error);
  }
};

export const loadGML = async (file) => {
  try {
    const convertedGeojson = await handleFileOpen(file);
    return convertedGeojson;
  } catch (error) {
    console.error('Error loading GML:', error);
    throw error;
  }
};

export const setGMLLayer = (
  layerIndex,
  gml,
  setHoverInfo,
  setWfsLayers,
  setViewState
) => {
  setWfsLayers(prev => [
    ...prev,
    new GeoJsonLayer({
      id: `gml-${layerIndex}`,
      data: gml,
      source: "catalog",
      opacity: 1,
      pickable: true,
      stroked: true,
      filled: true,
      visible: true,
      pointType: 'circle',
      lineWidthMinPixels: 2,
      lineWidthScale: 2,
      getFillColor: [255, 0, 0, 100],
      getLineColor: [255, 0, 0, 200],
      getPointRadius: 2,
      getLineWidth: 2,
      onHover: info => setHoverInfo(info),
      onError: (error) => console.error('GeoJsonLayer error:', error),
    })
  ]);

  if (gml && gml.features && gml.features.length > 0) {
    const [minX, minY, maxX, maxY] = bbox(gml);
    const centerLon = (minX + maxX) / 2;
    const centerLat = (minY + maxY) / 2;
    const lonRange = maxX - minX;
    const latRange = maxY - minY;
    const zoom = Math.log2(360 / Math.max(lonRange, latRange));

    showMapConfirmToast({
      title: "Pozicioniranje mape",
      message: "Da li želite da se prikaz mape pozicionira prema učitanom GML sloju?",
      confirmText: "Da",
      cancelText: "Ne",
      variant: "info",
      onConfirm: () => {
        setViewState(prev => ({
          ...prev,
          longitude: centerLon,
          latitude: centerLat,
          zoom,
          pitch: 0,
          bearing: 0,
          transitionDuration: 1000,
        }));
      },
    });
  }
};